import React, { useState, useRef, useEffect } from 'react';
import { Send, X, Bot, User, MapPin } from 'lucide-react';
import { traqPackAPI } from '../lib/supabase';
import BatteryBar from './BatteryBar';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  intent?: any;
  data?: any[];
}

interface AIChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AIChatPanel({ isOpen, onClose }: AIChatPanelProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Detect if this is likely a follow-up question
  const detectFollowUpQuestion = (query: string, previousMessages: Message[]): boolean => {
    const lowerQuery = query.toLowerCase();
    
    // Common follow-up patterns
    const followUpPatterns = [
      'what time zone',
      'timezone',
      'how long',
      'when did it leave',
      'when did it arrive',
      'is it still there',
      'how far',
      'which device',
      'which one',
      'that device',
      'this device',
      'the same',
      'it',
    ];
    
    // Check if query contains pronouns or references that need context
    const hasReference = followUpPatterns.some(pattern => lowerQuery.includes(pattern));
    
    // Check if it's a very short query that likely refers to previous context
    const isShortQuery = query.split(' ').length <= 4;
    
    // Check if previous message mentioned a device or location
    const hasPreviousContext = previousMessages.length > 0 && 
      previousMessages[previousMessages.length - 1].role === 'assistant';
    
    return hasReference || (isShortQuery && hasPreviousContext);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Check if this might be a follow-up question
      const isFollowUp = detectFollowUpQuestion(input, messages);
      
      let response;
      if (isFollowUp && messages.length > 0) {
        // Pass conversation history only for follow-up questions
        const conversationHistory = messages.slice(-2).map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp,
          intent: msg.intent
        }));
        response = await traqPackAPI.queryWithAI(input, conversationHistory, true);
      } else {
        // Normal query without history
        response = await traqPackAPI.queryWithAI(input);
      }
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
        intent: response.intent,
        data: response.data,
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl z-[99999] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gray-50">
        <div className="flex items-center gap-3">
          <Bot className="w-6 h-6 text-yellow-600" />
          <h2 className="text-lg font-semibold">TraqPack AI Assistant</h2>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 mt-8">
            <Bot className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-sm">Ask me about your devices!</p>
            <div className="mt-4 space-y-2">
              <p className="text-xs text-gray-400">Try asking:</p>
              <div className="space-y-1">
                <button
                  onClick={() => setInput("Where is my Kia?")}
                  className="text-xs text-blue-600 hover:underline block mx-auto"
                >
                  "Where is my Kia?"
                </button>
                <button
                  onClick={() => setInput("Was my Tesla in San Jose yesterday?")}
                  className="text-xs text-blue-600 hover:underline block mx-auto"
                >
                  "Was my Tesla in San Jose yesterday?"
                </button>
                <button
                  onClick={() => setInput("Show me all devices from last week")}
                  className="text-xs text-blue-600 hover:underline block mx-auto"
                >
                  "Show me all devices from last week"
                </button>
              </div>
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex gap-3 ${
              message.role === 'user' ? 'justify-end' : 'justify-start'
            }`}
          >
            {message.role === 'assistant' && (
              <div className="flex-shrink-0">
                <Bot className="w-8 h-8 text-yellow-600 bg-yellow-50 rounded-lg p-1.5" />
              </div>
            )}
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                message.role === 'user'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              {message.intent && (message.intent.type === 'battery_today' || message.intent.type?.startsWith('battery_at_time')) && message.data && message.data[0] && typeof (message.data[0] as any).battery_percentage === 'number' && (
                <div className="mt-2">
                  <BatteryBar percentage={(message.data[0] as any).battery_percentage} />
                </div>
              )}
              
              {/* Show location data if available */}
              {message.data && message.data.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <p className="text-xs opacity-75 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {message.data.length} location{message.data.length > 1 ? 's' : ''} found
                  </p>
                </div>
              )}
              
              <p className="text-xs mt-2 opacity-60">
                {message.timestamp.toLocaleTimeString()}
              </p>
            </div>
            {message.role === 'user' && (
              <div className="flex-shrink-0">
                <User className="w-8 h-8 text-gray-600 bg-gray-200 rounded-lg p-1.5" />
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="flex gap-3 justify-start">
            <Bot className="w-8 h-8 text-yellow-600 bg-yellow-50 rounded-lg p-1.5" />
            <div className="bg-gray-100 rounded-lg p-3">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t p-4">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about your devices..."
            className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="p-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}